import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Ticket, Clock, Tag, ArrowRight, Search } from 'lucide-react';
import { getTickets, updateTicket } from '../api/tickets';
import { toast } from 'sonner';
import Button from './ui/button';
import Input from './ui/input';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { cn } from '../lib/utils';

const TicketList = ({ 
  title = 'Tickets',
  filters = {},
  showFilters = true,
  showSearch = true,
  emptyMessage = 'No tickets found'
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  
  // Parse query parameters
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [assigneeSearch, setAssigneeSearch] = useState('');
  
  // Merge initial filters with URL query parameters
  const [localFilters, setLocalFilters] = useState(() => {
    const from = queryParams.get('from');
    const to = queryParams.get('to');
    const merged = { ...filters };
    if (from) merged.date_from = from;
    if (to) merged.date_to = to;
    return merged;
  });

  const [employees, setEmployees] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, pages: 0 });
  const isPendingView = localFilters.status === 'pending';
  const normalizedRole = String(user?.role || '').toLowerCase();
  
  // Sync URL params to state when URL changes
  useEffect(() => {
    const from = queryParams.get('from');
    const to = queryParams.get('to');
    if (from || to) {
      setLocalFilters(prev => ({
        ...prev,
        date_from: from || prev.date_from,
        date_to: to || prev.date_to
      }));
    }
  }, [queryParams]);
  const filteredEmployees = employees.filter((emp) =>
    !assigneeSearch.trim()
      ? true
      : String(emp.name || '').toLowerCase().includes(assigneeSearch.trim().toLowerCase()) ||
        String(emp.email || '').toLowerCase().includes(assigneeSearch.trim().toLowerCase())
  );

  useEffect(() => {
    fetchTickets();
    if (showFilters) fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localFilters, pagination.page]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const params = { ...localFilters, page: pagination.page, limit: pagination.limit };

      // "pending" is a derived status (open + in_progress), not a real DB status
      if (params.status === 'pending') {
        params.status = 'open,in_progress';
      }

      if (params.unassigned) { params.unassigned = true; delete params.assigned_to; }
      Object.keys(params).forEach(key => !params[key] && delete params[key]);

      const response = await getTickets(params);
      if (response.success) {
        let filteredTickets = response.data.tickets || [];

        if (searchTerm) {
          const lowerTerm = searchTerm.toLowerCase();
          filteredTickets = filteredTickets.filter(t =>
            t.subject?.toLowerCase().includes(lowerTerm) ||
            t.description?.toLowerCase().includes(lowerTerm) ||
            t.ticket_number?.toLowerCase().includes(lowerTerm) ||
            t.customer_name?.toLowerCase().includes(lowerTerm)
          );
        }
        setTickets(filteredTickets);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination?.total || filteredTickets.length,
          pages: response.data.pagination?.pages || 1
        }));
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const tenantScope = user?.tenant_id || 1;
      const response = await api.get(
        `/users?role=employee,admin&assignment_tenant_id=${tenantScope}`
      );
      if (response.data.success) setEmployees(response.data.data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleQuickAssign = async (ticketId, employeeId) => {
    try {
      const response = await updateTicket(ticketId, {
        assigned_to: employeeId,
        status: employeeId ? 'in_progress' : 'open',
      });
      if (response.success) {
        toast.success('Ticket assigned');
        fetchTickets();
      }
    } catch (error) {
      toast.error('Failed to assign');
    }
  };

  // Modern "Glassy" Badges - Works in Dark & Light Mode
  const getStatusStyles = (status) => {
    const styles = {
      open: 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 border-yellow-500/20',
      in_progress: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
      resolved: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',
      closed: 'bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/20',
      cancelled: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20',
    };
    return styles[status] || styles.closed;
  };

  const getPriorityStyles = (priority) => {
    const styles = {
      urgent: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800',
      high: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-800',
      medium: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800',
      low: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800',
    };
    return styles[priority] || styles.low;
  };

  return (
    <div className="p-4 md:p-6 min-h-screen bg-gray-50/50 dark:bg-slate-900 transition-colors duration-300">
      {/* Header – compact on mobile */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-5 md:mb-8 bg-white dark:bg-slate-900/60 p-4 rounded-2xl border border-gray-200/60 dark:border-gray-700 shadow-sm">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white">
            {title}
          </h1>
          <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            {pagination.total} tickets active
          </p>
        </div>
        {(normalizedRole === 'customer') && (
          <Button
            onClick={() => navigate('/tickets/create')}
            className="w-full md:w-auto text-sm shadow-lg shadow-blue-500/20"
          >
            + New Ticket
          </Button>
        )}
      </div>

      {/* Search & Filters Card */}
      {(showSearch || showFilters) && (
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 shadow-sm p-3 md:p-5 mb-6 md:mb-8 transition-all hover:shadow-md">
          <div className="flex flex-col md:flex-row gap-3 md:gap-4">
            {showSearch && (
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  placeholder="Search tickets..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 h-11 bg-gray-50 dark:bg-slate-900 border-gray-200 dark:border-slate-700 focus:ring-blue-500 focus:border-blue-500 transition-all"
                />
              </div>
            )}
            {showFilters && (
               <div className="flex gap-4 overflow-x-auto pb-2 md:pb-0">
                  <select
                    className="h-11 px-4 rounded-xl bg-gray-50 dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-sm font-medium focus:ring-2 focus:ring-blue-500"
                    onChange={(e) => setLocalFilters(p => ({...p, status: e.target.value}))}
                  >
                    <option value="">Status: All</option>
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                  </select>
                  
                  <select
                    className="h-11 px-4 rounded-xl bg-gray-50 dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-sm font-medium focus:ring-2 focus:ring-blue-500"
                    onChange={(e) => setLocalFilters(p => ({...p, priority: e.target.value}))}
                  >
                    <option value="">Priority: All</option>
                    <option value="urgent">Urgent</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                  {(normalizedRole === 'admin' || normalizedRole === 'employee') && (
                    <Input
                      placeholder="Search assignee..."
                      value={assigneeSearch}
                      onChange={(e) => setAssigneeSearch(e.target.value)}
                      className="h-11 min-w-[180px] bg-gray-50 dark:bg-slate-900 border-gray-200 dark:border-slate-700 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    />
                  )}
               </div>
            )}
          </div>
        </div>
      )}

      {/* Ticket List */}
      {loading ? (
        <div className="py-20 flex justify-center">
          <div className="w-10 h-10 border-4 border-blue-500/30 border-t-blue-600 rounded-full animate-spin" />
        </div>
      ) : tickets.length === 0 ? (
        <div className="text-center py-20 bg-white dark:bg-slate-800 rounded-3xl border border-dashed border-gray-300 dark:border-slate-700">
          <div className="bg-blue-50 dark:bg-blue-900/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <Ticket className="w-8 h-8 text-blue-500" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">No tickets found</h3>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Try adjusting your filters or search terms.</p>
        </div>
      ) : isPendingView ? (
        <>
          {/* Mobile: keep card layout for pending */}
          <div className="space-y-4 md:hidden">
            {tickets.map((ticket, idx) => (
              <div
                key={ticket.id}
                className="group relative bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-5 hover:shadow-xl hover:shadow-blue-500/5 hover:-translate-y-1 transition-all duration-300 cursor-pointer overflow-hidden"
                onClick={() => navigate(`/tickets/${ticket.id}`)}
                style={{ animationDelay: `${idx * 50}ms` }}
              >
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-blue-500 to-indigo-600 transform scale-y-0 group-hover:scale-y-100 transition-transform duration-300 origin-bottom" />
                <div className="flex flex-col gap-4 justify-between">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-mono text-gray-400 dark:text-gray-500">#{ticket.ticket_number}</span>
                      <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors line-clamp-1">
                        {ticket.subject}
                      </h3>
                    </div>
                    <p className="text-gray-600 dark:text-gray-400 text-sm line-clamp-2">
                      {ticket.description}
                    </p>
                    <div className="flex flex-wrap items-center gap-3 pt-2">
                      <span className={cn("px-3 py-1 rounded-full text-xs font-semibold border uppercase tracking-wide", getStatusStyles(ticket.status))}>
                        {ticket.status.replace('_', ' ')}
                      </span>
                      <span className={cn("px-3 py-1 rounded-full text-xs font-semibold border uppercase tracking-wide", getPriorityStyles(ticket.priority))}>
                        {ticket.priority}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      <Clock className="inline w-3 h-3 mr-1" />
                      {new Date(ticket.created_at).toLocaleDateString()}
                    </span>
                    {(normalizedRole === 'admin' || normalizedRole === 'employee') && (
                      <div onClick={e => e.stopPropagation()} className="w-full max-w-[180px]">
                        <select
                          className="w-full bg-gray-50 dark:bg-slate-900 border-none text-xs text-gray-600 dark:text-gray-300 font-medium rounded-lg focus:ring-2 focus:ring-blue-500 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                          value={ticket.assigned_to || ''}
                          onChange={(e) => handleQuickAssign(ticket.id, e.target.value)}
                        >
                          <option value="">Assign Agent...</option>
                          {filteredEmployees.map(emp => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
                        </select>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop: full-width table for pending tickets */}
          <div className="hidden md:block overflow-hidden rounded-2xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-50/60 dark:bg-slate-800/60 border-b border-gray-200 dark:border-slate-700 text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    <th className="p-3 w-[90px] font-semibold">Number</th>
                    <th className="p-3 w-[260px] font-semibold">Subject</th>
                    <th className="p-3 w-[120px] font-semibold">Opened</th>
                    <th className="p-3 w-[120px] font-semibold">Status</th>
                    <th className="p-3 w-[110px] font-semibold">Priority</th>
                    <th className="p-3 w-[160px] font-semibold">Category</th>
                    <th className="p-3 w-[180px] font-semibold">Assigned To</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
                  {tickets.map((ticket) => (
                    <tr
                      key={ticket.id}
                      onClick={() => navigate(`/tickets/${ticket.id}`)}
                      className="group bg-white dark:bg-slate-900 hover:bg-indigo-50/40 dark:hover:bg-indigo-900/10 transition-all duration-150 cursor-pointer"
                    >
                      <td className="p-3 font-mono text-xs font-medium text-indigo-600 dark:text-indigo-400 whitespace-nowrap">
                        {ticket.ticket_number || 'N/A'}
                      </td>
                      <td className="p-3 text-sm text-gray-900 dark:text-white">
                        <div className="font-medium line-clamp-1 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                          {ticket.subject || 'No subject'}
                        </div>
                        {ticket.description && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1 mt-0.5">
                            {ticket.description}
                          </div>
                        )}
                      </td>
                      <td className="p-3 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        <div>{new Date(ticket.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</div>
                        <div>{new Date(ticket.created_at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}</div>
                      </td>
                      <td className="p-3 whitespace-nowrap">
                        <span className={cn("px-3 py-1 rounded-full text-xs font-semibold border uppercase tracking-wide", getStatusStyles(ticket.status))}>
                          {ticket.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="p-3 whitespace-nowrap">
                        <span className={cn("px-3 py-1 rounded-full text-xs font-semibold border uppercase tracking-wide", getPriorityStyles(ticket.priority))}>
                          {ticket.priority}
                        </span>
                      </td>
                      <td className="p-3 text-xs text-gray-600 dark:text-gray-300 whitespace-nowrap">
                        {ticket.category_name || 'General'}
                      </td>
                      <td className="p-3 whitespace-nowrap" onClick={e => e.stopPropagation()}>
                        {(normalizedRole === 'admin' || normalizedRole === 'employee') ? (
                          ticket.assigned_to_name ? (
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-[10px] text-white font-bold shadow-sm">
                                {(ticket.assigned_to_name || '').replace(/0+$/, '').trim().charAt(0).toUpperCase()}
                              </div>
                              <span className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate max-w-[120px]">
                                {(ticket.assigned_to_name || '').replace(/0+$/, '').trim()}
                              </span>
                            </div>
                          ) : (
                            <select
                              className="w-full text-xs border border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-800 rounded-md py-1.5 px-2 text-gray-700 dark:text-gray-200 focus:ring-2 focus:ring-indigo-500/40 cursor-pointer"
                              value={ticket.assigned_to || ''}
                              onChange={(e) => handleQuickAssign(ticket.id, e.target.value)}
                            >
                              <option value="">Assign...</option>
                              {filteredEmployees.map(emp => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
                            </select>
                          )
                        ) : (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {ticket.assigned_to_name ? (ticket.assigned_to_name || '').replace(/0+$/, '').trim() : 'Unassigned'}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : (
        <div className="space-y-4">
          {tickets.map((ticket, idx) => (
            <div
              key={ticket.id}
              className="group relative bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-5 hover:shadow-xl hover:shadow-blue-500/5 hover:-translate-y-1 transition-all duration-300 cursor-pointer overflow-hidden"
              onClick={() => navigate(`/tickets/${ticket.id}`)}
              style={{ animationDelay: `${idx * 50}ms` }}
            >
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-blue-500 to-indigo-600 transform scale-y-0 group-hover:scale-y-100 transition-transform duration-300 origin-bottom" />
              <div className="flex flex-col md:flex-row md:items-center gap-4 justify-between">
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-mono text-gray-400 dark:text-gray-500">#{ticket.ticket_number}</span>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors line-clamp-1">
                      {ticket.subject}
                    </h3>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 text-sm line-clamp-2 md:line-clamp-1 pr-4">
                    {ticket.description}
                  </p>
                  <div className="flex flex-wrap items-center gap-3 pt-2">
                    <span className={cn("px-3 py-1 rounded-full text-xs font-semibold border uppercase tracking-wide", getStatusStyles(ticket.status))}>
                      {ticket.status.replace('_', ' ')}
                    </span>
                    <span className={cn("px-3 py-1 rounded-full text-xs font-semibold border uppercase tracking-wide", getPriorityStyles(ticket.priority))}>
                      {ticket.priority}
                    </span>
                    <div className="hidden md:flex items-center text-xs text-gray-500 dark:text-gray-400 ml-2 gap-3">
                      {ticket.category_name && (
                        <span className="flex items-center"><Tag className="w-3 h-3 mr-1" />{ticket.category_name}</span>
                      )}
                      <span className="flex items-center"><Clock className="w-3 h-3 mr-1" />{new Date(ticket.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between md:justify-end gap-4 min-w-[200px]">
                  {(normalizedRole === 'admin' || normalizedRole === 'employee') ? (
                    <div onClick={e => e.stopPropagation()} className="w-full md:w-auto">
                      <select
                        className="w-full bg-gray-50 dark:bg-slate-900 border-none text-sm text-gray-600 dark:text-gray-300 font-medium rounded-lg focus:ring-2 focus:ring-blue-500 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                        value={ticket.assigned_to || ''}
                        onChange={(e) => handleQuickAssign(ticket.id, e.target.value)}
                      >
                        <option value="">Assign Agent...</option>
                        {filteredEmployees.map(emp => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
                      </select>
                    </div>
                  ) : (
                    <div className="text-right">
                      <span className="text-xs text-gray-400 block mb-1">Status</span>
                      <ArrowRight className="w-5 h-5 text-gray-300 group-hover:text-blue-500 group-hover:translate-x-1 transition-all" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modern Pagination */}
      {pagination.pages > 1 && (
        <div className="mt-8 flex justify-center items-center gap-4">
          <Button
            variant="outline"
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
            disabled={pagination.page === 1}
            className="rounded-full w-10 h-10 p-0 flex items-center justify-center"
          >
            ←
          </Button>
          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
            Page <span className="text-gray-900 dark:text-white font-bold">{pagination.page}</span> of {pagination.pages}
          </span>
          <Button
            variant="outline"
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
            disabled={pagination.page === pagination.pages}
             className="rounded-full w-10 h-10 p-0 flex items-center justify-center"
          >
            →
          </Button>
        </div>
      )}
    </div>
  );
};

export default TicketList;
